<template>
    <div :class="[isValid ? 'valid' : '']">
        <vue-select
            v-model="internalValue"
            :options="mappedCountries"
            :placeholder="placeholder"
            :components="{ OpenIndicator }"
            :class="['wide-dropdown', internalValue ? '' : 'not-selected']"
            :get-option-label="getOptionLabel"
            :disabled="disabled"
            :filter-by="filterBy"
            @open="toggleLabelVisibility(true)"
            @close="toggleLabelVisibility(false)"
        >
            <template #selected-option="{ key, prefix }">
                <template v-if="key">
                    <span
                        :class="`option-flag country-flag fi fi-${key.toLowerCase()}`"
                    />
                    <span :class="'option-prefix'">{{ prefix }}</span>
                </template>
            </template>

            <template #option="{ key, prefix, name }">
                <template v-if="key">
                    <span
                        :class="`option-flag country-flag fi fi-${key.toLowerCase()}`"
                    />
                    <span :class="'option-name'">{{ name }}</span>
                    <span :class="'option-prefix'">{{ prefix }}</span>
                </template>
            </template>
        </vue-select>
        <span
            class="custom-select__label"
            :style="{ opacity: isLabelVisible ? 1 : 0 }"
        >
            {{ label }}
        </span>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, computed, h, PropType } from 'vue';
import VueSelect from 'vue-select';
import countriesData from '@/json/phonePrefixes.json';
import { useI18n } from 'vue-i18n';

type CountryOption = {
    value: string;
    key: string;
    prefix: string;
    name: string;
    label: string;
};

export default defineComponent({
    name: 'PhonePrefixSelect',
    components: { VueSelect },
    props: {
        modelValue: {
            type: String,
            default: '',
        },
        onlyCountries: {
            type: Array as PropType<string[]>,
            default: () => [],
        },
        label: {
            type: String,
            default: '',
        },
        placeholder: {
            type: String,
            default: '',
        },
        isValid: {
            type: Boolean,
            default: false,
        },
        defaultCountry: {
            type: String,
            default: '',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
        const internalValue = ref<CountryOption | null>(null);
        const isLabelVisible = ref(!!props.modelValue);
        const { locale } = useI18n();

        const mappedCountries = computed<CountryOption[]>(() => {
            const displayNames = new Intl.DisplayNames([locale.value], {
                type: 'region',
            });
            return props.onlyCountries
                .filter((code) =>
                    Object.prototype.hasOwnProperty.call(countriesData, code)
                )
                .map((code) => {
                    const country = (countriesData as Record<string, any>)[
                        code
                    ];
                    const translatedName =
                        displayNames.of(code) ?? country.name;
                    return {
                        value: code,
                        key: code,
                        prefix: country.prefix,
                        name: translatedName,
                        label: country.prefix,
                    };
                })
                .sort((a, b) => a.name.localeCompare(b.name, locale.value));
        });

        const getOptionLabel = (option: CountryOption) => option.label;

        const filterBy = (
            option: CountryOption,
            _label: string,
            search: string
        ) => {
            const normalizedSearch = search.trim().toLocaleLowerCase();

            if (!normalizedSearch) {
                return true;
            }

            return [option.prefix, option.name, option.key].some((value) =>
                value.toLocaleLowerCase().includes(normalizedSearch)
            );
        };

        const OpenIndicator = {
            render() {
                return h('span', { class: 'custom-select__input-arrow' });
            },
        };

        function toggleLabelVisibility(isOpen: boolean) {
            if (isOpen) {
                isLabelVisible.value = true;
            } else {
                isLabelVisible.value = !!internalValue.value;
            }
        }

        watch(
            () => props.modelValue,
            (newVal) => {
                if (newVal) {
                    internalValue.value =
                        mappedCountries.value.find(
                            (option) => option.prefix === newVal
                        ) || null;
                } else if (props.defaultCountry) {
                    const defaultOption =
                        mappedCountries.value.find(
                            (option) =>
                                option.key.toUpperCase() ===
                                props.defaultCountry.toUpperCase()
                        ) || null;
                    internalValue.value = defaultOption;
                    if (defaultOption) {
                        emit('update:modelValue', defaultOption.prefix);
                    }
                } else {
                    internalValue.value = null;
                }
            },
            { immediate: true }
        );

        watch(
            mappedCountries,
            (newMapped) => {
                if (!internalValue.value && props.defaultCountry) {
                    const defaultOption = newMapped.find(
                        (option) =>
                            option.key.toUpperCase() ===
                            props.defaultCountry.toUpperCase()
                    );
                    if (defaultOption) {
                        internalValue.value = defaultOption;
                    }
                }
            },
            { immediate: true }
        );

        watch(internalValue, (newVal) => {
            if (newVal) {
                emit('update:modelValue', newVal.prefix);
            } else {
                emit('update:modelValue', '');
            }
        });

        return {
            internalValue,
            isLabelVisible,
            mappedCountries,
            toggleLabelVisibility,
            OpenIndicator,
            getOptionLabel,
            filterBy,
        };
    },
});
</script>

<style scoped>
.custom-select__label {
    transition: opacity 0.2s;
}
.option-flag {
    margin-right: 8px;
}
.option-prefix {
    margin-left: 7px;
}
:deep(.wide-dropdown .vs__dropdown-menu) {
    width: 300px !important;
}

:deep(.vs--disabled) {
    pointer-events: none;
    opacity: 0.7;
}
</style>
